import { useEffect, useState } from 'react';
import { 
  X, 
  Phone, 
  Mail, 
  Building2, 
  Calendar,
  MessageSquare,
  Send,
  PhoneCall,
  FileText,
  Users,
  ChevronRight,
  Edit2,
  Save,
  MapPin,
  Clock,
  ExternalLink
} from 'lucide-react';
import { Lead, PIPELINE_STAGES, PipelineStage, Communication, getStageConfig, normalizeStatus } from '../lib/pocketbase';
import { useStore } from '../lib/store';
import { formatDate, formatRelative, formatPhone, cn } from '../lib/utils';

interface LeadDetailProps {
  lead: Lead;
  onClose: () => void;
}

export default function LeadDetail({ lead, onClose }: LeadDetailProps) {
  const { 
    communications, 
    fetchCommunications, 
    addCommunication,
    updateLead,
    updateLeadStatus
  } = useStore();
  
  const [activeTab, setActiveTab] = useState<'info' | 'history' | 'notes'>('info');
  const [newComm, setNewComm] = useState({ type: 'note' as Communication['type'], content: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({
    name: lead.name || '',
    email: lead.email || '',
    phone: lead.phone || '',
    company: lead.company || '',
    location: lead.location || '',
    notes: lead.notes || '',
    next_followup: lead.next_followup || '',
  });
  
  useEffect(() => {
    fetchCommunications(lead.id);
  }, [lead.id]);
  
  // Update edit data when lead changes
  useEffect(() => {
    setEditData({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      company: lead.company || '',
      location: lead.location || '',
      notes: lead.notes || '',
      next_followup: lead.next_followup || '',
    });
  }, [lead]);
  
  const handleAddCommunication = async () => {
    if (!newComm.content.trim()) return;
    
    try {
      await addCommunication({
        lead: lead.id,
        type: newComm.type,
        direction: 'outbound',
        content: newComm.content,
      });
      setNewComm({ type: 'note', content: '' });
    } catch (err) {
      console.error('Error adding communication:', err);
    }
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateLead(lead.id, editData);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating lead:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  const commTypeIcons = {
    email: Mail,
    call: PhoneCall,
    sms: MessageSquare,
    meeting: Users,
    note: FileText,
  };
  
  const commTypeLabels = {
    email: 'Email',
    call: 'Appel',
    sms: 'SMS',
    meeting: 'RDV',
    note: 'Note',
  };
  
  const currentStage = getStageConfig(lead.status);
  
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 animate-fadeIn"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white h-full overflow-hidden flex flex-col animate-slideIn shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {lead.name || lead.company || 'Lead'}
              </h2>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                {lead.activity && <span className="truncate">{lead.activity}</span>}
                {lead.activity && lead.location && <span>•</span>}
                {lead.location && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3" />
                    {lead.location}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 -m-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Quick actions */}
          <div className="flex items-center gap-2 mt-4">
            {lead.email && (
              <a
                href={`mailto:${lead.email}`}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email
              </a>
            )}
            {lead.phone && (
              <a
                href={`tel:${lead.phone}`}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                Appeler
              </a>
            )}
          </div>
        </div>
        
        {/* Status selector */}
        <div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-b border-gray-200">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            Statut
          </label>
          <div className="flex gap-2 flex-wrap">
            {PIPELINE_STAGES.map(stage => {
              const isActive = normalizeStatus(lead.status) === stage.id;
              return (
                <button
                  key={stage.id}
                  onClick={() => updateLeadStatus(lead.id, stage.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                    isActive
                      ? `${stage.color} text-white shadow-sm`
                      : `${stage.bgLight} ${stage.textColor} hover:opacity-80`
                  )}
                >
                  <span>{stage.emoji}</span>
                  {stage.label}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex-shrink-0 px-6 border-b border-gray-200 bg-white">
          <div className="flex gap-6">
            {[
              { id: 'info', label: 'Informations' },
              { id: 'history', label: 'Historique' },
              { id: 'notes', label: 'Notes' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={cn(
                  'py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tab content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-6">
            {activeTab === 'info' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Contact</h3>
                  <button
                    onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                  >
                    {isEditing ? (
                      <>
                        <Save className="w-4 h-4" />
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                      </>
                    ) : (
                      <>
                        <Edit2 className="w-4 h-4" />
                        Modifier
                      </>
                    )}
                  </button>
                </div>
                
                <div className="space-y-3">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Nom</label>
                        <input
                          type="text"
                          value={editData.name}
                          onChange={e => setEditData({ ...editData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Entreprise</label>
                        <input
                          type="text"
                          value={editData.company}
                          onChange={e => setEditData({ ...editData, company: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Email</label>
                        <input
                          type="email"
                          value={editData.email}
                          onChange={e => setEditData({ ...editData, email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Téléphone</label>
                        <input
                          type="tel"
                          value={editData.phone}
                          onChange={e => setEditData({ ...editData, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Localisation</label>
                        <input
                          type="text"
                          value={editData.location}
                          onChange={e => setEditData({ ...editData, location: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Prochaine relance</label>
                        <input
                          type="date"
                          value={editData.next_followup}
                          onChange={e => setEditData({ ...editData, next_followup: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {lead.email && (
                        <a 
                          href={`mailto:${lead.email}`}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                        >
                          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Mail className="w-4 h-4 text-gray-400 group-hover:text-primary-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-0.5">Email</p>
                            <p className="text-sm text-gray-900 truncate">{lead.email}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                        </a>
                      )}
                      {lead.phone && (
                        <a 
                          href={`tel:${lead.phone}`}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                        >
                          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Phone className="w-4 h-4 text-gray-400 group-hover:text-green-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-0.5">Téléphone</p>
                            <p className="text-sm text-gray-900">{formatPhone(lead.phone)}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                        </a>
                      )}
                      {lead.company && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Building2 className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-0.5">Entreprise</p>
                            <p className="text-sm text-gray-900 truncate">{lead.company}</p>
                          </div>
                        </div>
                      )}
                      {lead.location && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <MapPin className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-0.5">Localisation</p>
                            <p className="text-sm text-gray-900 truncate">{lead.location}</p>
                          </div>
                        </div>
                      )}
                      {lead.next_followup && (
                        <div className="flex items-center gap-3 p-3 bg-primary-50 rounded-lg border border-primary-100">
                          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Calendar className="w-4 h-4 text-primary-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-primary-600 mb-0.5">Prochaine relance</p>
                            <p className="text-sm text-primary-700 font-medium">
                              {formatDate(lead.next_followup, "EEEE d MMMM yyyy")}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {lead.description && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                      Description du projet
                    </h3>
                    <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                      {lead.description}
                    </p>
                  </div>
                )}
                
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                    Métadonnées
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Créé le {formatDate(lead.created)}</span>
                    </div>
                    {lead.last_contact && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Phone className="w-4 h-4" />
                        <span>Dernier contact {formatRelative(lead.last_contact)}</span>
                      </div>
                    )}
                    {lead.source && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <ExternalLink className="w-4 h-4" />
                        <span>Source : {lead.source}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'history' && (
              <div className="space-y-4">
                {/* Add communication form */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex gap-1">
                    {(['note', 'email', 'call', 'sms', 'meeting'] as const).map(type => {
                      const Icon = commTypeIcons[type];
                      return (
                        <button
                          key={type}
                          onClick={() => setNewComm({ ...newComm, type })}
                          className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                            newComm.type === type
                              ? 'bg-primary-500 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100'
                          )}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">{commTypeLabels[type]}</span>
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={newComm.content}
                    onChange={e => setNewComm({ ...newComm, content: e.target.value })}
                    placeholder="Ajouter une note ou un résumé de communication..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none"
                    rows={3}
                  />
                  <button
                    onClick={handleAddCommunication}
                    disabled={!newComm.content.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    Ajouter
                  </button>
                </div>
                
                {/* Communications list */}
                <div className="space-y-3">
                  {communications.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <MessageSquare className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">Aucune communication enregistrée</p>
                    </div>
                  ) : (
                    communications.map(comm => {
                      const Icon = commTypeIcons[comm.type] || FileText;
                      return (
                        <div 
                          key={comm.id}
                          className="flex gap-3 p-4 bg-white border border-gray-200 rounded-xl"
                        >
                          <div className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                            comm.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'
                          )}>
                            <Icon className={cn(
                              'w-4 h-4',
                              comm.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'
                            )} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-gray-900">
                                {commTypeLabels[comm.type]}
                              </span>
                              <span className={cn(
                                'text-xs px-1.5 py-0.5 rounded',
                                comm.direction === 'inbound' 
                                  ? 'bg-green-50 text-green-600' 
                                  : 'bg-blue-50 text-blue-600'
                              )}>
                                {comm.direction === 'inbound' ? 'Entrant' : 'Sortant'}
                              </span>
                              <span className="text-xs text-gray-400 ml-auto">
                                {formatRelative(comm.created)}
                              </span>
                            </div>
                            {comm.subject && (
                              <p className="text-sm font-medium text-gray-700 mb-1">{comm.subject}</p>
                            )}
                            <p className="text-sm text-gray-600 whitespace-pre-wrap">{comm.content}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'notes' && (
              <div className="space-y-4">
                <textarea
                  value={editData.notes}
                  onChange={e => setEditData({ ...editData, notes: e.target.value })}
                  placeholder="Notes internes sur ce lead..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none min-h-[300px]"
                />
                <button
                  onClick={() => updateLead(lead.id, { notes: editData.notes })}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Enregistrer les notes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
